import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getMediaById, getSimilarMedia, formatViews } from "@/lib/mediaQuery";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";
import MediaCard from "@/components/feature/MediaCard";

export default function DetailPage() {
  const { id = "" } = useParams();
  const item = getMediaById(id);
  const [toast, setToast] = useState("");

  const similar = useMemo(() => (item ? getSimilarMedia(item, 4) : []), [item]);
  const relatedNews = useMemo(() => newsList.filter((n) => n.relatedId === id), [id]);

  useSeo({
    title: item ? `${item.title}剧情介绍与资讯 - 缘之空` : "影视信息暂不可用 - 缘之空",
    description: item
      ? `《${item.title}》由${item.director}执导，${item.actors.join("、")}主演，${item.year}年${item.region}${item.type}。${item.synopsis}`
      : "该影视信息暂不可用，请返回缘之空首页浏览其他影视作品。",
    keywords: item ? `缘之空,${item.title},${item.title}剧情,${item.genres.join(",")},影视资讯` : "缘之空,影视详情",
  });

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">影视信息暂不可用</h1>
        <p className="text-sm text-foreground-500 mb-6">这部影视可能已下架或链接有误，你可以返回首页继续浏览。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const categoryLinks = [
    { label: `${item.type}分类`, to: `/category/${item.type === "电影" ? "movie" : "tv"}`, icon: "ri-film-line" },
    { label: `${item.region}影视`, to: `/category/${item.region === "国产" ? "china" : "overseas"}`, icon: "ri-global-line" },
    ...item.genres.slice(0, 2).map((g) => ({ label: `${g}标签`, to: `/search?q=${encodeURIComponent(g)}`, icon: "ri-price-tag-3-line" })),
  ];

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <Link to={`/category/${item.type === "电影" ? "movie" : "tv"}`} className="hover:text-primary-600 transition-colors">
            {item.type}
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">{item.title}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-6 md:gap-8 mb-10">
          <div className="w-full max-w-[280px] md:max-w-none mx-auto md:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 高清影视封面`}
                title={`${item.title} 剧情介绍`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                onClick={() => setToast("收藏功能仅为前端演示，暂未接入账号存储")}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-line"></i>
                收藏
              </button>
              <button
                type="button"
                onClick={() => setToast("分享功能仅为前端演示，链接已生成占位")}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs">{item.type}</span>
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">{item.region}</span>
              <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-600 text-xs font-label">{item.year}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-2">{item.title}</h1>
            <p className="text-sm text-foreground-400 font-label mb-5">{item.enTitle}</p>

            <div className="flex flex-wrap items-center gap-6 mb-6">
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-3xl font-bold text-accent-600">{item.rating.toFixed(1)}</span>
                <span className="text-xs text-foreground-400">/ 10</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-eye-line text-primary-500"></i>
                {formatViews(item.views)} 次浏览
              </div>
              <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                <i className="ri-time-line text-primary-500"></i>
                {item.duration}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {item.genres.map((g) => (
                <span key={g} className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs">
                  {g}
                </span>
              ))}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm border-t border-background-200 pt-5">
              <div className="flex gap-2">
                <dt className="text-foreground-400 shrink-0 w-12">导演</dt>
                <dd className="text-foreground-800">{item.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-foreground-400 shrink-0 w-12">上映</dt>
                <dd className="text-foreground-800">{item.year} 年</dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="text-foreground-400 shrink-0 w-12">主演</dt>
                <dd className="text-foreground-800">{item.actors.join("、")}</dd>
              </div>
            </dl>
          </div>
        </div>

        <section className="mb-10">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            剧情简介
          </h2>
          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{item.synopsis}</p>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            分类入口
          </h2>
          <div className="flex flex-wrap gap-3">
            {categoryLinks.map((l) => (
              <Link
                key={l.to + l.label}
                to={l.to}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className={`${l.icon} text-primary-500`}></i>
                {l.label}
              </Link>
            ))}
          </div>
        </section>

        {relatedNews.length > 0 && (
          <section className="mb-10">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              影视资讯动态
            </h2>
            <ul className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-200 overflow-hidden">
              {relatedNews.map((n) => (
                <li key={n.id} className="px-5 py-4 hover:bg-primary-50/60 transition-colors">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-xs">{n.category}</span>
                    <span className="text-xs text-foreground-400 font-label">{n.date}</span>
                  </div>
                  <h3 className="text-sm md:text-base font-medium text-foreground-900">{n.title}</h3>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{n.excerpt}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {similar.length > 0 && (
          <section>
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              相关影视推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {similar.map((s, i) => (
                <MediaCard key={s.id} item={s} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm shadow-none animate-fade-in">
          <i className="ri-information-line mr-1.5"></i>
          {toast}
        </div>
      )}
    </div>
  );
}